import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { ZapIcon, UsersIcon, CalendarIcon, BellIcon, SparklesIcon, ClipboardIcon, InfoIcon, RocketIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import '../styles/ClubAdminDashboard.css';

interface ActivityItem {
  id: string;
  type: 'member_join' | 'event_create' | 'announcement' | 'discussion';
  title: string;
  description: string;
  timestamp: string;
}

interface DashboardMetric {
  label: string;
  value: string;
  change: string;
  color: string;
  icon: React.ReactNode;
}

interface StudentPerformancePoint {
  name: string;
  attendanceScore: number;
  engagementScore: number;
}

interface ManagedClub {
  id: string;
  name: string;
}

interface ForumChannel {
  id: string;
  name: string;
  members: number;
  online: number;
}

interface ChannelMessage {
  id: string;
  senderName: string;
  content: string;
  createdAt?: string;
}

interface OnlineUser {
  id: string;
  username: string;
  lastSeen?: number;
}

const ClubAdminDashboard: React.FC = () => {
  const { token, user } = useAuth();
  const { subscribe } = useWebSocket();
  const [activeTab, setActiveTab] = useState('overview');
  const [announcementText, setAnnouncementText] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [announcementStatus, setAnnouncementStatus] = useState('');
  const [channelInputText, setChannelInputText] = useState('');
  const [clubsLoading, setClubsLoading] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [roomLoading, setRoomLoading] = useState(false);
  const [adminClubs, setAdminClubs] = useState<ManagedClub[]>([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [channels, setChannels] = useState<ForumChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [channelMessages, setChannelMessages] = useState<ChannelMessage[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const API_URL = BACKEND_URL;

  const metrics: DashboardMetric[] = [
    {
      label: 'Total Members',
      value: '1,248',
      change: '+5.2%',
      color: 'neon-cyan',
      icon: <UsersIcon size={20} />
    },
    {
      label: 'Active Events',
      value: '14',
      change: 'STABLE',
      color: 'neon-magenta',
      icon: <CalendarIcon size={20} />
    },
    {
      label: 'Engagement %',
      value: '68.4%',
      change: '+12%',
      color: 'neon-purple',
      icon: <ZapIcon size={20} />
    },
    {
      label: 'Growth Velocity',
      value: 'High',
      change: 'OPTIMAL',
      color: 'neon-cyan',
      icon: <ClipboardIcon size={20} />
    }
  ];

  const activityLog: ActivityItem[] = [
    {
      id: '1',
      type: 'member_join',
      title: 'USER_JOIN',
      description: 'Nova_Prime-99 entered the matrix.',
      timestamp: '14:22:01'
    },
    {
      id: '2',
      type: 'event_create',
      title: 'EVENT_CRT',
      description: '"Quantum AI Hack" added to registry.',
      timestamp: '14:15:34'
    },
    {
      id: '3',
      type: 'announcement',
      title: 'ANNOUNCEMENT',
      description: 'Global announcement sent to 1,248 members.',
      timestamp: '13:45:00'
    },
    {
      id: '4',
      type: 'discussion',
      title: 'DISCUSSION_TOPIC',
      description: 'New discussion started in #general-discussion.',
      timestamp: '13:30:22'
    },
    {
      id: '5',
      type: 'member_join',
      title: 'USER_JOIN',
      description: 'Dev_Zero linked to node DEL_SOUTH_01.',
      timestamp: '12:15:55'
    }
  ];

  const memberEngagement = [
    { name: 'Nova_Prime', status: 'Active', attendance: '92%', lastSeen: '2 min ago' },
    { name: 'Dev_Zero', status: 'Active', attendance: '85%', lastSeen: '15 min ago' },
    { name: 'Cipher_X', status: 'Idle', attendance: '76%', lastSeen: '2 hours ago' },
    { name: 'Echo_Bot', status: 'Offline', attendance: '65%', lastSeen: '1 day ago' }
  ];

  const eventStats = [
    { name: 'Quantum AI Hack', registrations: 342, trending: '+18%' },
    { name: 'Cyber Security Summit', registrations: 289, trending: '+12%' },
    { name: 'Neural Network Workshop', registrations: 156, trending: '+8%' }
  ];

  const clubNews = [
    {
      title: 'AI_MODEL_UPDATE_V4',
      content: 'The latest neural engine has been integrated into the forum bots...'
    },
    {
      title: 'SUBSCRIPTION_REWARDS',
      content: 'New badge icons available for Pro level members in the store...'
    }
  ];

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'member_join': return '#00f3ff';
      case 'event_create': return '#ff00ff';
      case 'announcement': return '#bc13fe';
      case 'discussion': return '#4ade80';
      default: return '#00f3ff';
    }
  };

  const getActivityLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      member_join: 'USER_JOIN',
      event_create: 'EVENT_CRT',
      announcement: 'ANNOUNCE',
      discussion: 'DISCUSSION'
    };
    return labels[type] || 'ACTION';
  };

  const parseAttendance = (value: string) => Number(value.replace('%', '').trim()) || 0;

  const getRecencyScore = (lastSeen: string) => {
    const normalized = lastSeen.toLowerCase();
    if (normalized.includes('min')) return 100;
    if (normalized.includes('hour')) return 72;
    return 45;
  };

  const getStatusScore = (status: string) => {
    const statusMap: { [key: string]: number } = {
      active: 100,
      idle: 68,
      offline: 42
    };
    return statusMap[status.toLowerCase()] ?? 55;
  };

  // Student performance analysis combines attendance and engagement signals.
  const studentPerformanceAnalysis: StudentPerformancePoint[] = memberEngagement.map((member) => {
    const attendanceScore = parseAttendance(member.attendance);
    const recencyScore = getRecencyScore(member.lastSeen);
    const statusScore = getStatusScore(member.status);
    const engagementScore = Math.round((attendanceScore * 0.45) + (statusScore * 0.35) + (recencyScore * 0.2));

    return {
      name: member.name,
      attendanceScore,
      engagementScore
    };
  });

  const buildTrendPath = (values: number[]) => {
    if (values.length === 0) return '';

    const chartStartX = 70;
    const chartEndX = 550;
    const chartTop = 50;
    const chartBottom = 280;
    const chartHeight = chartBottom - chartTop;
    const chartWidth = chartEndX - chartStartX;

    const points = values.map((value, index) => {
      const x = values.length === 1
        ? chartStartX + chartWidth / 2
        : chartStartX + (index * chartWidth) / (values.length - 1);
      const normalizedValue = Math.max(0, Math.min(100, value));
      const y = chartBottom - (normalizedValue / 100) * chartHeight;
      return { x, y };
    });

    // Create zig-zag path (straight lines instead of curves)
    return points.reduce((path, point, index) => {
      if (index === 0) return `M${point.x},${point.y}`;
      return `${path} L${point.x},${point.y}`;
    }, '');
  };

  const attendanceTrendPath = buildTrendPath(studentPerformanceAnalysis.map((item) => item.attendanceScore));
  const engagementTrendPath = buildTrendPath(studentPerformanceAnalysis.map((item) => item.engagementScore));

  const selectedChannel = channels.find((channel) => channel.id === selectedChannelId) || null;
  const selectedClub = adminClubs.find((club) => club.id === selectedClubId) || null;
  const authToken = token || localStorage.getItem('token');
  const currentUsername = user?.username || 'Club Admin';

  const buildAuthConfig = () => ({
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    timeout: 12000
  });

  const normalizeChannelName = (name: string) =>
    name
      .trim()
      .toLowerCase()
      .replace(/^#+/, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const sendPresenceHeartbeat = async (channelId: string) => {
    if (!channelId || !authToken) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/channels/${channelId}/presence`, {}, buildAuthConfig());
    } catch (error) {
      console.error('Failed to update presence heartbeat:', error);
    }
  };

  const fetchChannelRoom = async (channelId: string) => {
    if (!channelId || !authToken) {
      return;
    }

    try {
      setRoomLoading(true);
      await sendPresenceHeartbeat(channelId);

      const [messagesResponse, onlineResponse] = await Promise.all([
        axios.get(`${API_URL}/api/channels/${channelId}/messages`, { ...buildAuthConfig(), params: { page: 0, size: 50 } }),
        axios.get(`${API_URL}/api/channels/${channelId}/online-users`, buildAuthConfig())
      ]);

      const messagesData = messagesResponse.data;
      const messages = Array.isArray(messagesData.messages) ? messagesData.messages : [];
      // Reverse to display oldest first in chronological order
      setChannelMessages(messages.reverse());
      setOnlineUsers(Array.isArray(onlineResponse.data) ? onlineResponse.data : []);
    } catch (error) {
      console.error('Failed to fetch channel room:', error);
      setAnnouncementStatus('Unable to sync channel in real time right now.');
    } finally {
      setRoomLoading(false);
    }
  };

  const fetchChannels = async (clubId: string) => {
    if (!clubId || !authToken) {
      return;
    }

    try {
      setChannelsLoading(true);
      const response = await axios.get(`${API_URL}/api/clubs/${clubId}/channels`, buildAuthConfig());
      const loadedChannels = Array.isArray(response.data) ? response.data : [];
      setChannels(loadedChannels);

      if (loadedChannels.length === 0) {
        setSelectedChannelId('');
        setChannelMessages([]);
        setOnlineUsers([]);
        return;
      }

      const keepSelected = loadedChannels.some((channel: ForumChannel) => channel.id === selectedChannelId);
      const nextChannelId = keepSelected ? selectedChannelId : loadedChannels[0].id;
      setSelectedChannelId(nextChannelId);
    } catch (error) {
      console.error('Failed to fetch channels:', error);
      setAnnouncementStatus('Failed to load channels for this club.');
      setChannels([]);
      setSelectedChannelId('');
    } finally {
      setChannelsLoading(false);
    }
  };

  const fetchAdminClubs = async () => {
    if (!authToken) {
      return;
    }

    try {
      setClubsLoading(true);
      const response = await axios.get(`${API_URL}/api/clubs`, buildAuthConfig());
      const clubs = Array.isArray(response.data) ? response.data : [];
      setAdminClubs(clubs);

      if (clubs.length === 0) {
        setSelectedClubId('');
        setAnnouncementStatus('No clubs found for this admin account.');
        return;
      }

      const hasSelected = clubs.some((club: ManagedClub) => club.id === selectedClubId);
      setSelectedClubId(hasSelected ? selectedClubId : clubs[0].id);
    } catch (error) {
      console.error('Failed to fetch admin clubs:', error);
      setAnnouncementStatus('Failed to load your clubs.');
    } finally {
      setClubsLoading(false);
    }
  };

  const handleSelectChannel = async (channelId: string) => {
    if (!authToken) {
      return;
    }

    setSelectedChannelId(channelId);
    try {
      await axios.post(`${API_URL}/api/channels/${channelId}/join`, {}, buildAuthConfig());
    } catch (error) {
      console.error('Failed to auto-join selected channel:', error);
    }
    await fetchChannelRoom(channelId);
  };

  const handleBroadcastAnnouncement = async () => {
    const cleanText = announcementText.trim();
    if (!cleanText) {
      setAnnouncementStatus('Enter announcement text before broadcasting.');
      return;
    }

    const textParts = cleanText.split(' - ');
    const title = textParts[0]?.trim() || cleanText;
    const description = textParts.slice(1).join(' - ').trim() || 'Yes';

    try {
      await axios.post(
        `${API_URL}/api/announcements`,
        {
          title,
          description
        },
        {
          headers: {
            Authorization: `Bearer ${token || localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setAnnouncementText('');
      setAnnouncementStatus('Announcement broadcasted to student terminal.');
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        setAnnouncementStatus('You are not authorized to broadcast announcements.');
        return;
      }
      setAnnouncementStatus('Failed to broadcast announcement. Please retry.');
    }
  };

  const handleCreateChannel = async () => {
    if (!selectedClubId || !authToken) {
      setAnnouncementStatus('Select a club before creating a channel.');
      return;
    }

    const normalizedName = normalizeChannelName(newChannelName);
    if (!normalizedName) {
      setAnnouncementStatus('Enter a valid channel name.');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/clubs/${selectedClubId}/channels`,
        { name: normalizedName },
        buildAuthConfig()
      );

      const createdId = response.data?.id || '';
      setNewChannelName('');
      setAnnouncementStatus(`Channel #${normalizedName} created.`);
      await fetchChannels(selectedClubId);
      if (createdId) {
        await handleSelectChannel(createdId);
      }
    } catch (error: any) {
      console.error('Failed to create channel:', error);
      setAnnouncementStatus(error?.response?.data?.error || 'Failed to create channel.');
    }
  };

  const handleSendChannelMessage = async () => {
    if (!authToken || !selectedChannel) {
      setAnnouncementStatus('Select a channel first.');
      return;
    }

    const text = channelInputText.trim();
    if (!text) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/channels/${selectedChannel.id}/join`, {}, buildAuthConfig());
      const response = await axios.post(
        `${API_URL}/api/channels/${selectedChannel.id}/messages`,
        { content: text },
        buildAuthConfig()
      );

      // Add the message directly from server response
      const newMessage = response.data as ChannelMessage;
      setChannelMessages((prev) => {
        // Prevent duplicates if WebSocket already added it
        if (prev.some((msg) => msg.id === newMessage.id)) {
          return prev;
        }
        return [...prev, newMessage];
      });
      
      setChannelInputText('');

      if (selectedClubId) {
        await fetchChannels(selectedClubId);
      }
    } catch (error: any) {
      console.error('Failed to send channel message:', error);
      setAnnouncementStatus(error?.response?.data?.error || 'Failed to send message.');
    }
  };

  useEffect(() => {
    if (!authToken) {
      return;
    }
    fetchAdminClubs();
  }, [authToken]);

  useEffect(() => {
    if (!selectedClubId || !authToken) {
      return;
    }
    fetchChannels(selectedClubId);
  }, [selectedClubId, authToken]);

  useEffect(() => {
    if (!selectedChannelId || !authToken || activeTab !== 'forums') {
      return;
    }

    // Initial fetch
    fetchChannelRoom(selectedChannelId);

    // Subscribe to new messages via WebSocket (replaces 2-second polling!)
    const unsubscribeMessages = subscribe(`/topic/channel/${selectedChannelId}/messages`, (newMessage: ChannelMessage) => {
      setChannelMessages((prev) => {
        // Prevent duplicates - check if message already exists
        if (prev.some((msg) => msg.id === newMessage.id)) {
          return prev;
        }
        return [...prev, newMessage];
      });
    });

    // Cleanup
    return () => {
      unsubscribeMessages?.();
    };
  }, [selectedChannelId, authToken, activeTab, subscribe]);

  return (
    <div className="club-admin-dashboard">
      {/* Sidebar Navigation */}
      <aside className="club-sidebar">
        <div className="sidebar-header">
          <h1 className="sidebar-title">CLUB_CORE</h1>
          <p className="sidebar-subtitle">Admin_v1.0</p>
        </div>
        
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <ClipboardIcon size={18} />
            <span>Overview</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <ZapIcon size={18} />
            <span>Analytics</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'forums' ? 'active' : ''}`}
            onClick={() => setActiveTab('forums')}
          >
            <BellIcon size={18} />
            <span>Forums</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="club-main-content">
        {/* Header */}
        <header className="club-header">
          <h2 className="header-title">DASHBOARD<span className="cyber-accent">.sys</span></h2>
        </header>

        {/* Content Area */}
        <div className="dashboard-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tab-content"
            >
              {/* Metrics Grid */}
              <section className="metrics-section">
                <div className="metrics-grid">
                  {metrics.map((metric, idx) => (
                    <motion.div
                      key={idx}
                      className={`metric-card metric-${metric.color}`}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="metric-header">
                        <span className="metric-icon">{metric.icon}</span>
                        <p className="metric-label">{metric.label}</p>
                      </div>
                      <div className="metric-value">{metric.value}</div>
                      <div className="metric-change">{metric.change}</div>
                      <div className="metric-bar">
                        <div className={`bar-fill bar-${metric.color}`}></div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>

              {/* Activity Log & Analytics Row */}
              <section className="analytics-section">
                {/* Activity Log */}
                <div className="activity-log-card">
                  <div className="card-header">
                    <InfoIcon size={16} />
                    <h3>Activity_Log</h3>
                  </div>
                  <div className="activity-feed">
                    {activityLog.map((item) => (
                      <div key={item.id} className="activity-item">
                        <span className="activity-time">[{item.timestamp}]</span>
                        <span
                          className="activity-type"
                          style={{ color: getActivityColor(item.type) }}
                        >
                          {getActivityLabel(item.type)}:
                        </span>
                        <span className="activity-desc">{item.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Member Engagement */}
                <div className="engagement-card">
                  <div className="card-header">
                    <UsersIcon size={16} />
                    <h3>Member_Engagement</h3>
                  </div>
                  <div className="engagement-list">
                    {memberEngagement.map((member, idx) => (
                      <div key={idx} className="engagement-item">
                        <div className="member-info">
                          <p className="member-name">{member.name}</p>
                          <p className="member-meta">{member.lastSeen}</p>
                        </div>
                        <div className="member-stats">
                          <span className={`status ${member.status.toLowerCase()}`}>
                            {member.status}
                          </span>
                          <span className="attendance">{member.attendance}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Event Stats */}
                <div className="event-stats-card">
                  <div className="card-header">
                    <CalendarIcon size={16} />
                    <h3>Event_Statistics</h3>
                  </div>
                  <div className="event-list">
                    {eventStats.map((event, idx) => (
                      <div key={idx} className="event-item">
                        <div className="event-info">
                          <p className="event-name">{event.name}</p>
                          <p className="event-registrations">{event.registrations} registrations</p>
                        </div>
                        <span className="event-trending">{event.trending}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </motion.div>
          )}

          {/* Forums Tab */}
          {activeTab === 'forums' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tab-content"
            >
              <section className="forums-section">
                {/* Announcements/Broadcast */}
                <div className="forum-card announcements-card">
                  <h3 className="forum-title">Global_Broadcaster</h3>
                  <div className="broadcaster-container">
                    <textarea
                      className="broadcaster-input"
                      placeholder="SEND_GLOBAL_ANNOUNCEMENT..."
                      value={announcementText}
                      onChange={(e) => setAnnouncementText(e.target.value)}
                      rows={3}
                    />
                    <div className="broadcaster-actions">
                      <button className="btn-primary" onClick={handleBroadcastAnnouncement}>
                        <SparklesIcon size={16} />
                        BROADCAST
                      </button>
                    </div>
                    {announcementStatus && (
                      <p className="forum-status-text">{announcementStatus}</p>
                    )}
                  </div>
                </div>

                <div className="forums-grid">
                  {/* Channels */}
                  <div className="forum-card channels-card discord-channels-panel">
                    <h4 className="forum-subtitle">Active_Channels</h4>
                    <p className="channel-hint-text">
                      {clubsLoading
                        ? 'Loading clubs...'
                        : selectedClub
                          ? `Managing channels for ${selectedClub.name}`
                          : 'Select a club to manage channels.'}
                    </p>
                    <div className="create-channel" style={{ marginTop: '10px' }}>
                      <select
                        className="channel-input"
                        value={selectedClubId}
                        onChange={(e) => setSelectedClubId(e.target.value)}
                        disabled={clubsLoading || adminClubs.length === 0}
                      >
                        {adminClubs.map((club) => (
                          <option key={club.id} value={club.id}>{club.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="channels-list">
                      {channelsLoading && <p className="chat-empty">Syncing channels...</p>}
                      {!channelsLoading && channels.length === 0 && <p className="chat-empty">No channels yet.</p>}
                      {channels.map((channel) => (
                        <button
                          key={channel.id}
                          className={`channel-item ${selectedChannel?.id === channel.id ? 'active' : ''}`}
                          onClick={() => handleSelectChannel(channel.id)}
                        >
                          <span className="channel-item-meta">
                            <span className="channel-name">#{channel.name}</span>
                            <span className="online-count">{channel.online} online</span>
                          </span>
                          <span className="channel-enter-tag">
                            {selectedChannel?.id === channel.id ? 'ENTERED' : 'ENTER'}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="create-channel">
                      <input
                        type="text"
                        placeholder="NEW_CHANNEL_NAME..."
                        value={newChannelName}
                        onChange={(e) => setNewChannelName(e.target.value)}
                        className="channel-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateChannel();
                          }
                        }}
                      />
                      <button className="btn-secondary small" onClick={handleCreateChannel}>CREATE</button>
                    </div>
                    <p className="channel-hint-text">Click any channel to enter its chat area. Joined members of that channel appear on the right.</p>
                  </div>

                  {/* Channel Room */}
                  <div className="forum-card news-card channel-room-card discord-chat-panel">
                    <div className="chat-room-header">
                      <h4 className="forum-subtitle">Channel_Room</h4>
                      <span className="active-channel-label">#{selectedChannel?.name || 'select-a-channel'}</span>
                      <span className="online-count">{selectedChannel?.online || 0} online now</span>
                    </div>
                    <div className="news-list channel-messages-list chat-messages-scroll">
                      {roomLoading && <p className="chat-empty">Syncing chat...</p>}
                      {!roomLoading && channelMessages.length === 0 && (
                        <p className="chat-empty">No messages yet. Start the conversation.</p>
                      )}
                      {channelMessages.map((message) => (
                        <div key={message.id} className="news-item channel-message-item chat-message-row">
                          <div className="chat-message-topline">
                            <span className="chat-author">{message.senderName}</span>
                            <span className="chat-time">
                              [{message.createdAt
                                ? new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : '--:--'}]
                            </span>
                          </div>
                          <p className="news-content chat-text">{message.content}</p>
                        </div>
                      ))}
                    </div>
                    <div className="create-channel channel-message-composer chat-composer">
                      <input
                        type="text"
                        placeholder="TYPE_MESSAGE..."
                        value={channelInputText}
                        onChange={(e) => setChannelInputText(e.target.value)}
                        className="channel-input"
                        disabled={!selectedChannel}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSendChannelMessage();
                          }
                        }}
                      />
                      <button className="btn-primary small" onClick={handleSendChannelMessage} disabled={!selectedChannel}>SEND</button>
                    </div>
                  </div>

                  {/* Joined Members */}
                  <div className="forum-card discord-members-panel">
                    <h4 className="forum-subtitle">Joined_Members</h4>
                    <p className="member-count-text">{onlineUsers.length} online in #{selectedChannel?.name || 'channel'}</p>
                    <div className="member-list-panel">
                      {onlineUsers.length === 0 && (
                        <p className="chat-empty">No active users right now.</p>
                      )}
                      {onlineUsers.map((member) => (
                        <div key={`${selectedChannel?.id}-${member.username}`} className="member-row-item">
                          <span className="member-presence-dot" />
                          <span className="member-row-name">
                            {member.username}
                            {member.username === currentUsername ? ' (you)' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="forum-card">
                  <h4 className="forum-subtitle">Club_News_Feed</h4>
                  <div className="news-list">
                    {clubNews.map((news, idx) => (
                      <div key={idx} className="news-item">
                        <p className="news-title">{news.title}</p>
                        <p className="news-content">{news.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </motion.div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tab-content"
            >
              <section className="analytics-detailed">
                <div className="chart-container">
                  <div className="card-header">
                    <ZapIcon size={16} />
                    <h3>ENGAGEMENT_TRENDS</h3>
                  </div>
                  <div className="chart-wrapper">
                    <svg 
                      className="trend-chart" 
                      viewBox="0 0 600 350" 
                      preserveAspectRatio="xMidYMid meet"
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <defs>
                        <marker id="arrowCyan" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
                          <path d="M0,0 L0,6 L9,3 z" fill="#00f3ff" />
                        </marker>
                        <marker id="arrowMagenta" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
                          <path d="M0,0 L0,6 L9,3 z" fill="#ff00ff" />
                        </marker>
                      </defs>
                      {/* Grid Lines */}
                      {[0, 1, 2, 3, 4].map((i) => (
                        <motion.line
                          key={`hgrid-${i}`}
                          x1="60"
                          y1={50 + i * 50}
                          x2="560"
                          y2={50 + i * 50}
                          stroke="rgba(0, 243, 255, 0.08)"
                          strokeWidth="0.5"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.1 }}
                        />
                      ))}
                      {studentPerformanceAnalysis.map((_, i) => (
                        <motion.line
                          key={`vgrid-${i}`}
                          x1={70 + (i * 480) / (studentPerformanceAnalysis.length - 1 || 1)}
                          y1="50"
                          x2={70 + (i * 480) / (studentPerformanceAnalysis.length - 1 || 1)}
                          y2="280"
                          stroke="rgba(255, 0, 255, 0.05)"
                          strokeWidth="0.5"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.08 }}
                        />
                      ))}

                      {/* X-Axis */}
                      <line x1="60" y1="280" x2="560" y2="280" stroke="#ff00ff" strokeWidth="1.5" />

                      {/* Y-Axis Labels (Score) */}
                      {[0, 25, 50, 75, 100].map((val) => {
                        const y = 280 - (val / 100) * 230;
                        return (
                          <g key={`ylabel-${val}`}>
                            <text
                              x="45"
                              y={y + 4}
                              fontSize="11"
                              fill="#00f3ff"
                              textAnchor="end"
                              fontFamily="Share Tech Mono"
                            >
                              {val}
                            </text>
                            <line x1="55" y1={y} x2="60" y2={y} stroke="#00f3ff" strokeWidth="1" opacity="0.5" />
                          </g>
                        );
                      })}

                      {/* X-Axis Labels (Student Names) */}
                      {studentPerformanceAnalysis.map((item, i) => {
                        const chartStartX = 70;
                        const chartEndX = 550;
                        const x = chartStartX + (i * (chartEndX - chartStartX)) / (studentPerformanceAnalysis.length - 1 || 1);
                        return (
                          <motion.g key={`xlabel-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.1 }}>
                            <text
                              x={x}
                              y="300"
                              fontSize="10"
                              fill="#ff00ff"
                              textAnchor="middle"
                              fontFamily="Share Tech Mono"
                            >
                              {item.name.slice(0, 8)}
                            </text>
                            <line x1={x} y1="280" x2={x} y2="285" stroke="#ff00ff" strokeWidth="1" opacity="0.6" />
                          </motion.g>
                        );
                      })}

                      {/* Hoverable Bar Groups - Bars extend upward from purple x-axis line */}
                      {studentPerformanceAnalysis.map((item, i) => {
                        const chartStartX = 70;
                        const chartEndX = 550;
                        const x = chartStartX + (i * (chartEndX - chartStartX)) / (studentPerformanceAnalysis.length - 1 || 1);
                        
                        // Fixed boundaries
                        const xAxisY = 280;        // x-axis line position (y = 0)
                        const chartTop = 50;       // chart top (y = 100)
                        const maxHeight = xAxisY - chartTop; // 230 units
                        
                        // Clamp scores to 0-100
                        const attScore = Math.max(0, Math.min(100, item.attendanceScore));
                        const engScore = Math.max(0, Math.min(100, item.engagementScore));
                        
                        // Heights (positive only)
                        const attBarH = (attScore / 100) * maxHeight;
                        const engBarH = (engScore / 100) * maxHeight;
                        
                        // Top positions (bars sit on baseline at xAxisY, extend upward)
                        const attBarY = xAxisY - attBarH;
                        const engBarY = xAxisY - engBarH;
                        
                        const isHovered = hoveredIndex === i;

                        return (
                          <g
                            key={`bar-group-${i}`}
                            onMouseEnter={() => setHoveredIndex(i)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            style={{ cursor: 'pointer' }}
                          >
                            {/* Attendance Bar (Cyan) - bottom at xAxisY, extends upward */}
                            <motion.rect
                              x={x - 6}
                              width="5"
                              fill="#00f3ff"
                              opacity={isHovered ? 1 : 0.8}
                              initial={{ height: 0, y: xAxisY }}
                              animate={{ height: attBarH, y: attBarY }}
                              transition={{ duration: 0.6, delay: i * 0.1 }}
                            />

                            {/* Engagement Bar (Magenta) - bottom at xAxisY, extends upward */}
                            <motion.rect
                              x={x + 1}
                              width="5"
                              fill="#ff00ff"
                              opacity={isHovered ? 1 : 0.8}
                              initial={{ height: 0, y: xAxisY }}
                              animate={{ height: engBarH, y: engBarY }}
                              transition={{ duration: 0.6, delay: i * 0.12 }}
                            />

                            {/* Tooltip on Hover */}
                            {isHovered && (
                              <g>
                                <rect
                                  x={x - 28}
                                  y={Math.min(attBarY, engBarY) - 35}
                                  width="56"
                                  height="28"
                                  rx="2"
                                  fill="rgba(10, 12, 28, 0.9)"
                                  stroke="rgba(0, 243, 255, 0.5)"
                                  strokeWidth="1"
                                />
                                <text
                                  x={x}
                                  y={Math.min(attBarY, engBarY) - 17}
                                  fontSize="9"
                                  fill="#00f3ff"
                                  textAnchor="middle"
                                  fontFamily="Share Tech Mono"
                                >
                                  A: {attScore}
                                </text>
                                <text
                                  x={x}
                                  y={Math.min(attBarY, engBarY) - 6}
                                  fontSize="9"
                                  fill="#ff00ff"
                                  textAnchor="middle"
                                  fontFamily="Share Tech Mono"
                                >
                                  E: {engScore}
                                </text>
                              </g>
                            )}
                          </g>
                        );
                      })}

                      <motion.path
                        d={attendanceTrendPath}
                        fill="none"
                        stroke="#00f3ff"
                        strokeDasharray="2,2"
                        strokeWidth="2"
                        strokeLinecap="round"
                        markerEnd="url(#arrowCyan)"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.9 }}
                        transition={{ duration: 1.2, delay: 0.3 }}
                      />
                      <motion.path
                        d={engagementTrendPath}
                        fill="none"
                        stroke="#ff00ff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        markerEnd="url(#arrowMagenta)"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.9 }}
                        transition={{ duration: 1.2, delay: 0.5 }}
                      />

                      {/* Axis Labels */}
                      <text x="20" y="160" fontSize="12" fill="#00f3ff" fontFamily="Orbitron" writingMode="tb" textAnchor="middle">
                        SCORE
                      </text>
                      <text x="310" y="330" fontSize="12" fill="#ff00ff" fontFamily="Orbitron" textAnchor="middle">
                        MEMBERS
                      </text>
                    </svg>
                  </div>

                  {/* Legend */}
                  <motion.div
                    className="chart-legend"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <div className="legend-item">
                      <div className="legend-marker" style={{ backgroundColor: '#00f3ff', boxShadow: '0 0 12px rgba(0, 243, 255, 0.6)' }}></div>
                      <span>Attendance_Score</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-marker" style={{ backgroundColor: '#ff00ff', boxShadow: '0 0 12px rgba(255, 0, 255, 0.6)' }}></div>
                      <span>Engagement_Score</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-marker" style={{ borderTop: '2px dashed #00f3ff' }}></div>
                      <span>Attendance_Trend</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-marker" style={{ borderTop: '2px solid #ff00ff' }}></div>
                      <span>Engagement_Trend</span>
                    </div>
                  </motion.div>
                </div>
              </section>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClubAdminDashboard;
